import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Shop, ShopStatus } from '@reims-local/types';
import { adminApi } from '../lib/api';

export function Shops() {
  const [shops, setShops] = useState<Shop[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ShopStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.listShops({
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setShops(res.data);
      setError(null);
    } catch (err) {
      setError(String((err as Error).message));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function handleSendInvitation(shopId: string) {
    try {
      await adminApi.sendInvitation(shopId);
      await load();
      alert('Invitation envoyée.');
    } catch (err) {
      alert(`Erreur : ${(err as Error).message}`);
    }
  }

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Boutiques ({shops.length})</h2>
        <div className="spacer" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ShopStatus | '')}>
          <option value="">Tous statuts</option>
          <option value="pre_registered">Pré-inscrites</option>
          <option value="pending">En attente</option>
          <option value="active">Actives</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspendues</option>
        </select>
        <input
          placeholder="Rechercher nom ou email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void load();
          }}
          style={{ maxWidth: 260 }}
        />
        <button onClick={() => void load()}>Rechercher</button>
        <Link to="/shops/new">
          <button className="primary">+ Ajouter</button>
        </Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p>Chargement…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Type</th>
              <th>Statut</th>
              <th>Plan</th>
              <th>Partenaire</th>
              <th>Créée</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {shops.map((s) => (
              <tr key={s.id}>
                <td>
                  <Link to={`/shops/${s.id}`}>{s.name}</Link>
                  <div className="muted">{s.email ?? '—'}</div>
                </td>
                <td>{s.shop_type}</td>
                <td>
                  <span className={`badge ${statusClass(s.status)}`}>{statusLabel(s.status)}</span>
                </td>
                <td>{s.plan}</td>
                <td>{s.partner_included ? 'Vitrines' : '—'}</td>
                <td className="muted">{formatDate(s.created_at)}</td>
                <td>
                  {(s.status === 'pre_registered' || s.status === 'pending') && s.email ? (
                    <button onClick={() => void handleSendInvitation(s.id)}>Inviter</button>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))}
            {shops.length === 0 && (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                  Aucune boutique.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

function statusClass(s: ShopStatus): string {
  if (s === 'active') return 'green';
  if (s === 'suspended') return 'red';
  if (s === 'trial') return 'blue';
  return 'orange';
}

function statusLabel(s: ShopStatus): string {
  const map: Record<ShopStatus, string> = {
    pre_registered: 'Pré-inscrite',
    pending: 'En attente',
    active: 'Active',
    suspended: 'Suspendue',
    trial: 'Trial',
  };
  return map[s];
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch {
    return iso;
  }
}
